namespace $ {

	export type $shm_hitalama_map_access_outbox_entry = $shm_hitalama_map_access_invitation_data & {
		catalog: string
		pass: string
		intent?: { previous_time: number, rank: number, change: boolean }
	}

	/** Private durable publication journal. It never grants permissions itself. */
	export class $shm_hitalama_map_access_outbox extends $giper_baza_dict_to( $giper_baza_atom_dict ) {

		static entry( value: unknown ): $shm_hitalama_map_access_outbox_entry | null {
			const invitation = $shm_hitalama_map_access_invitation.parse( value )
			if( !invitation ) return null
			const data = value as $shm_hitalama_map_access_outbox_entry
			if( typeof data.catalog !== 'string' || typeof data.pass !== 'string' ) return null
			if( data.intent !== undefined && ( !data.intent || typeof data.intent !== 'object' || Array.isArray( data.intent )
				|| !Number.isSafeInteger( data.intent.previous_time ) || data.intent.previous_time < 0
				|| !Number.isInteger( data.intent.rank ) || data.intent.rank < 0 || data.intent.rank > 255
				|| typeof data.intent.change !== 'boolean' ) ) return null
			return $mol_error_fence( ()=> {
				const link = $giper_baza_link.check( data.catalog )
				if( !link?.lord().str || link.str !== link.land().str ) return null
				const pass = $giper_baza_auth_pass.from( data.pass )
				if( pass.lord().str !== invitation.recipient ) return null
				return { ... invitation, catalog: data.catalog, pass: data.pass }
			}, ()=> null )
		}

		@ $mol_mem
		pending() {
			return this.keys().flatMap( key=> {
				if( typeof key !== 'string' ) return []
				const data = $shm_hitalama_map_access_outbox.entry( this.key( key )?.val() )
				return data ? [ [ key, data ] as const ] : []
			} )
		}

		@ $mol_mem
		repair() {
			for( const [ key ] of this.pending() ) {
				this.$.$mol_error_fence( ()=> this.repair_entry( key ), ()=> null, ()=> null )
			}
			return null
		}

		@ $mol_mem_key
		repair_entry( key: string ) {
			// Observe the journal and rights outside the one-shot action: a false
			// result before give() must not become a permanently cached repair.
			const entry = $shm_hitalama_map_access_outbox.entry( this.key( key )?.val() )
			if( !entry ) return false
			const map = this.map_land( entry.map )
			const gift = map._gift.get( entry.recipient )
			const revision = gift ? `${ gift.lord().str }/${ gift.time_tick() }/${ gift.rank() }` : ''
			const retry = Math.floor( this.$.$mol_state_time.now( 1000 ) / 1000 ) * 1000
			return this.repair_revision([ key, JSON.stringify( entry ), revision, map.lord_rank( map.auth().pass().lord() ), retry ])
		}

		@ $mol_mem_key
		repair_revision( [ key, , , , retry ]: readonly [ string, string, string, number, number ] ) {
			// Keep the suspended action owned by a persistent channel. Catching
			// its Promise directly in a completed render abandons the temp fiber.
			return this.flush( key, retry )
		}

		@ $mol_action
		prepare( catalog: $shm_hitalama_map_access_catalog, map: $giper_baza_land,
			recipient: $giper_baza_auth_pass, active: boolean, title: string,
			rank: typeof $giper_baza_rank.Value, always_change = false ) {
			this.assert_private()
			const gift = map._gift.get( recipient.lord().str )
			const change = always_change || gift?.rank() !== rank
			const data: $shm_hitalama_map_access_outbox_entry = {
				version: 1, recipient: recipient.lord().str, map: map.link().str,
				// This is only a private preparation record, never an invitation.
				grant_time: gift?.time_tick() || 1, active, title,
				catalog: catalog.link().str, pass: recipient.toString(),
				intent: { previous_time: gift?.time_tick() ?? 0, rank, change },
			}
			const key = this.store( data )
			// Persist intent BEFORE give(): a crash after the grant but before
			// enqueue() must still leave enough information to publish it safely.
			this.land().units_saving()
			return change
		}

		assert_private() {
			if( this.land().lord_rank( null ) >= this.$.$giper_baza_rank_read
				|| this.land().lord_rank( this.land().auth().pass().lord() ) < this.$.$giper_baza_rank_rule ) {
				$mol_fail( new Error( 'Map invitation outbox must be private' ) )
			}
		}

		@ $mol_action
		store( data: $shm_hitalama_map_access_outbox_entry ) {
			this.assert_private()
			if( !$shm_hitalama_map_access_outbox.entry( data ) ) $mol_fail( new Error( 'Invalid map invitation publication' ) )
			const key = data.map + '/' + data.recipient
			const slot = this.key( key ) ?? this.key( key, 'auto' )!
			if( JSON.stringify( slot.val() ) !== JSON.stringify( data ) ) slot.val( data )
			return key
		}

		@ $mol_action
		enqueue( catalog: $shm_hitalama_map_access_catalog, map: $giper_baza_land,
			recipient: $giper_baza_auth_pass, active: boolean, title: string ) {
			this.assert_private()
			const gift = map._gift.get( recipient.lord().str )
			if( !gift ) $mol_fail( new Error( 'Map invitation requires a personal grant' ) )
			const data: $shm_hitalama_map_access_outbox_entry = {
				version: 1, recipient: recipient.lord().str, map: map.link().str,
				grant_time: gift.time_tick(), active, title,
				catalog: catalog.link().str, pass: recipient.toString(),
			}
			return this.store( data )
		}

		@ $mol_mem_key
		recipient( pass: string ) { return this.$.$giper_baza_auth_pass.from( pass ) }

		@ $mol_mem_key
		map_land( id: string ) { return this.$.$giper_baza_glob.Land( new this.$.$giper_baza_link( id ) ).sync() }

		@ $mol_mem_key
		catalog( id: string ) {
			return this.$.$giper_baza_glob.Pawn( new this.$.$giper_baza_link( id ), this.$.$shm_hitalama_map_access_catalog )
		}

		@ $mol_action
		flush( key: string, retry?: number ) {
			const slot = this.key( key )
			let entry = $shm_hitalama_map_access_outbox.entry( slot?.val() )
			if( !entry ) return false
			const map = this.map_land( entry.map )
			map.loading()
			const gift = map._gift.get( entry.recipient )
			if( entry.intent ) {
				const intent = entry.intent
				// No grant happened yet: do not grant anything during recovery.
				if( !gift || ( intent.change && gift.time_tick() === intent.previous_time ) ) return false
				if( gift.rank() !== intent.rank || gift.time_tick() < intent.previous_time
					|| ( intent.change && gift.lord().str !== map.auth().pass().lord().str ) ) {
					if( JSON.stringify( slot!.val() ) === JSON.stringify( entry ) ) slot!.val( null )
					return false
				}
				const { intent: _, ... confirmed } = entry
				entry = { ... confirmed, grant_time: gift.time_tick() }
				slot!.val( entry )
			}
			// Another command/administrator superseded this grant. Never restore
			// its permissions or relabel the newer gift as the old invitation.
			if( !gift || gift.time_tick() !== entry.grant_time
				|| map.lord_rank( map.auth().pass().lord() ) < this.$.$giper_baza_rank_rule ) {
				if( JSON.stringify( slot!.val() ) === JSON.stringify( entry ) ) slot!.val( null )
				return false
			}
			// Local persistence must precede acknowledgement. A restart can then
			// continue normal yard delivery even if the master was offline.
			this.$.$shm_hitalama_map_access_grant.record( map, entry.recipient, entry.active )
			this.land().units_saving()
			map.units_saving()
			const catalog = this.catalog( entry.catalog )
			const pass = this.recipient( entry.pass )
			catalog.publish( map, pass, entry.active, entry.title )
			const shard = catalog.shard( entry.recipient )
			shard.units_saving()
			const head = this.$.$shm_hitalama_map_access_index.recipient_head( entry.recipient )
			const self = this.$.$shm_hitalama_map_access_envelope.slot([ shard.auth(), pass, entry.map ])
			const sand = shard.sand_get( head, shard.auth().pass().lord(), self )
			if( !sand || !this.delivery_confirmed( entry, sand, retry ?? this.$.$mol_wire_sync( Date ).now() ) ) return false
			if( JSON.stringify( slot!.val() ) === JSON.stringify( entry ) ) slot!.val( null )
			return true
		}

		delivery_uri() { return '/hitalama_map_delivery' }

		delivery_confirmed( entry: $shm_hitalama_map_access_outbox_entry, sand: $giper_baza_unit_sand, time: number ) {
			return this.$.$mol_error_fence( ()=> this.$.$shm_hitalama_map_access_delivery.confirmed([
				this.land().auth(), entry.map, entry.catalog, entry.recipient, sand.self().str,
				sand.hash().str, entry.grant_time, time, this.delivery_uri(),
			]), ()=> false )
		}

	}

}
